"use client"

/* /cards sound. Nothing borrowed from the wheel: no samples, no riser, no
   win jingle, no ticks. Every sound here is a card moving air, made from
   noise through a moving bandpass, each one slightly different.

   - deal():  a card sliding off the stack into the fan. Short, soft, low.
   - flip():  a card turning over. Quick paper flick, brighter, with a snap.
   - fly():   a card swooping across the room. Longer, rising then falling.
   - land():  a card arriving. A short low puff, the air it pushes ahead.
   - pack():  the pack opening. One broad, slow whoosh. */

type Ctx = AudioContext

export interface CardsAudio {
  unlock: () => void
  deal: () => void
  flip: () => void
  fly: () => void
  land: () => void
  pack: () => void
}

interface Whoosh {
  /** seconds */
  dur: number
  /** bandpass start and end, Hz */
  from: number
  to: number
  /** peak of the curve, 0..1 of the duration */
  peakAt: number
  gain: number
  q: number
  /** highpass under it, Hz */
  floor: number
  /** a short bright transient at the peak (paper snap), 0 = none */
  snap: number
}

const rnd = (lo: number, hi: number) => lo + Math.random() * (hi - lo)

export function createCardsAudio(): CardsAudio {
  let ctx: Ctx | null = null
  let noise: AudioBuffer | null = null
  let master: GainNode | null = null

  function context(): Ctx | null {
    if (typeof window === "undefined") return null
    if (!ctx) {
      const Impl =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Impl) return null
      ctx = new Impl()
      master = ctx.createGain()
      master.gain.value = 0.85
      const comp = ctx.createDynamicsCompressor()
      comp.threshold.value = -16
      comp.knee.value = 10
      comp.ratio.value = 3
      comp.attack.value = 0.003
      comp.release.value = 0.12
      master.connect(comp).connect(ctx.destination)
    }
    return ctx
  }

  function ready(): Ctx | null {
    const c = context()
    if (!c) return null
    if (c.state === "suspended") void c.resume()
    return c
  }

  function noiseBuffer(c: Ctx): AudioBuffer {
    if (noise) return noise
    const len = c.sampleRate * 2
    const buf = c.createBuffer(1, len, c.sampleRate)
    const d = buf.getChannelData(0)
    // Pink-ish noise: paper moves air, it does not hiss.
    let b0 = 0, b1 = 0, b2 = 0
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1
      b0 = 0.99765 * b0 + w * 0.099046
      b1 = 0.963 * b1 + w * 0.2965164
      b2 = 0.57 * b2 + w * 1.0526913
      d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.2
    }
    noise = buf
    return buf
  }

  function whoosh(w: Whoosh) {
    const c = ready()
    if (!c || !master) return
    const t = c.currentTime
    const dur = w.dur * rnd(0.92, 1.08)
    const peak = t + dur * w.peakAt

    const src = c.createBufferSource()
    src.buffer = noiseBuffer(c)
    src.loop = true
    src.loopStart = rnd(0, 1.2)
    src.loopEnd = src.loopStart + 0.8

    const hp = c.createBiquadFilter()
    hp.type = "highpass"
    hp.frequency.value = w.floor

    const bp = c.createBiquadFilter()
    bp.type = "bandpass"
    bp.Q.value = w.q
    const from = w.from * rnd(0.9, 1.1)
    const to = w.to * rnd(0.9, 1.1)
    bp.frequency.setValueAtTime(from, t)
    bp.frequency.exponentialRampToValueAtTime(to, peak)
    bp.frequency.exponentialRampToValueAtTime(from * 0.8, t + dur)

    const g = c.createGain()
    g.gain.setValueAtTime(0.0001, t)
    g.gain.exponentialRampToValueAtTime(w.gain * rnd(0.85, 1.05), peak)
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur)

    src.connect(hp).connect(bp).connect(g).connect(master)
    src.start(t)
    src.stop(t + dur + 0.05)

    if (w.snap > 0) {
      // The card's edge catching the air: a few ms of bright noise at the peak.
      const s = c.createBufferSource()
      s.buffer = noiseBuffer(c)
      s.loopStart = rnd(0, 1.5)
      const sh = c.createBiquadFilter()
      sh.type = "highpass"
      sh.frequency.value = 2400
      const sg = c.createGain()
      sg.gain.setValueAtTime(0.0001, peak - 0.004)
      sg.gain.exponentialRampToValueAtTime(w.snap, peak)
      sg.gain.exponentialRampToValueAtTime(0.0001, peak + 0.035)
      s.connect(sh).connect(sg).connect(master)
      s.start(peak - 0.004, s.loopStart)
      s.stop(peak + 0.05)
    }
  }

  return {
    unlock: () => void ready(),
    deal: () =>
      whoosh({ dur: 0.22, from: 420, to: 1400, peakAt: 0.35, gain: 0.16, q: 1.1, floor: 180, snap: 0 }),
    flip: () =>
      whoosh({ dur: 0.18, from: 700, to: 2600, peakAt: 0.4, gain: 0.2, q: 1.4, floor: 300, snap: 0.09 }),
    fly: () =>
      whoosh({ dur: 0.62, from: 380, to: 2200, peakAt: 0.45, gain: 0.24, q: 1.0, floor: 150, snap: 0 }),
    land: () =>
      whoosh({ dur: 0.14, from: 900, to: 260, peakAt: 0.25, gain: 0.18, q: 0.9, floor: 120, snap: 0.05 }),
    pack: () =>
      whoosh({ dur: 0.9, from: 240, to: 1600, peakAt: 0.4, gain: 0.3, q: 0.8, floor: 90, snap: 0 }),
  }
}

export const cardsAudio: CardsAudio = createCardsAudio()
